import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import UseContext from 'hooks/useContext';

// What the api exports at once; more is refused with 400 INVOICE_NUMBERS_INVALID.
export const EXPORT_MAX_INVOICES = 1000;

const FAILURE_TEXTS = {
  PERIOD_INVALID: {
    key: 'exportPeriodInvalid',
    fallback: 'Ce mois ne peut pas être exporté.',
  },
  INVOICE_NUMBERS_INVALID: {
    key: 'exportSelectionInvalid',
    fallback: 'Cette sélection ne peut pas être exportée.',
  },
};
const DEFAULT_FAILURE = {
  key: 'exportFailed',
  fallback: 'L’export CSV n’a pas pu être fait, réessayez.',
};

/** Saves a Blob under `fileName`: the CSV export, and the PDF of the invoice detail (JBWEB-1274). */
export const downloadFile = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/**
 * The CSV export of invoices picked in the month list (JBWEB-1273). The file
 * is saved as "factures-2026-06.csv", named here: the name the api gives in
 * Content-Disposition cannot be read across origins. One export at a time:
 * `isExporting` is true until the api answers.
 */
const useInvoiceExport = () => {
  const { language } = UseContext();
  const [isExporting, setIsExporting] = useState(false);
  // Two clicks in one render would both see `isExporting` false.
  const isSending = useRef(false);

  const exportInvoices = useCallback(
    (period, numbers) => {
      if (isSending.current) return;
      isSending.current = true;
      setIsExporting(true);
      const done = () => {
        isSending.current = false;
        setIsExporting(false);
      };
      ApiDatabase.postInvoiceExport(
        { period, numbers },
        (blob) => {
          done();
          downloadFile(blob, `factures-${period}.csv`);
        },
        (error) => {
          done();
          if (error?.name === 'CanceledError') return;
          // Never the error object in the console: axios keeps the request, token included.
          const failure =
            FAILURE_TEXTS[error?.response?.data?.errorCode] ?? DEFAULT_FAILURE;
          toast.error(
            language.getValue(
              `invoiceList.selection.${failure.key}`,
              failure.fallback,
            ),
          );
        },
      );
    },
    [language],
  );

  return { exportInvoices, isExporting };
};

export default useInvoiceExport;
